import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { User } from '../../types/models';
import LoadingSpinner from '../../components/LoadingSpinner';
import { trackError, createErrorMeta } from '../../lib/telemetry';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<'email' | 'uid'>('email');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      return;
    }

    try {
      setLoading(true);
      setSearched(true);
      setSearchResults([]);

      if (searchType === 'email') {
        // Search by email
        const usersRef = collection(db, 'users');
        const emailQuery = query(usersRef, where('email', '==', searchQuery.trim()), limit(1));
        const snapshot = await getDocs(emailQuery);

        const results: User[] = [];
        snapshot.forEach(docSnap => {
          results.push({ ...docSnap.data(), uid: docSnap.id } as User);
        });
        setSearchResults(results);
      } else {
        // Search by UID
        const userDoc = await getDoc(doc(db, 'users', searchQuery.trim()));
        if (userDoc.exists()) {
          setSearchResults([{ ...userDoc.data(), uid: userDoc.id } as User]);
        } else {
          setSearchResults([]);
        }
      }
    } catch (error: any) {
      trackError(
        'User search failed',
        createErrorMeta('ui', 'ADMIN_USER_SEARCH', 'S3', 'degraded', '24h', {
          userAction: 'admin_user_search',
        }),
        error
      );
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Admin Console
        </button>
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <p className="mt-2 text-gray-600">
          Search for users by email or UID to view details and manage account flags.
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Type Toggle */}
          <div className="flex space-x-4 mb-4">
            <button
              type="button"
              onClick={() => setSearchType('email')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                searchType === 'email'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Search by Email
            </button>
            <button
              type="button"
              onClick={() => setSearchType('uid')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                searchType === 'uid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Search by UID
            </button>
          </div>

          {/* Search Input */}
          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchType === 'email' ? 'user@example.com' : 'User ID (UID)'}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={loading || !searchQuery.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {/* Search Results */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" text="Searching..." />
        </div>
      )}

      {!loading && searched && searchResults.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <svg
            className="mx-auto h-12 w-12 text-yellow-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L4.082 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No User Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No user found with that {searchType}. Please check the spelling and try again.
          </p>
        </div>
      )}

      {!loading && searchResults.length > 0 && (
        <div className="space-y-6">
          {searchResults.map(user => (
            <div
              key={user.uid}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              {/* User Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-600">
                      {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {user.displayName || 'Unknown User'}
                    </h2>
                    <p className="text-gray-600">{user.email}</p>
                    <p className="text-sm text-gray-400 font-mono">{user.uid}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/admin/users/${user.uid}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  View Details
                </button>
              </div>

              {/* Account Status */}
              {user.flags?.onHold && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800 font-medium">
                        Account On Hold -{' '}
                        {user.flags.reason === 'excessive_upload'
                          ? 'Excessive Upload'
                          : 'Manual Review'}
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Flagged on: {formatDate(user.flags.at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* User Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Billing Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Billing</h3>
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="font-medium">Plan:</span>{' '}
                      <span
                        className={
                          user.billing?.plan ? 'text-green-600 font-semibold' : 'text-gray-600'
                        }
                      >
                        {user.billing?.plan || 'FREE'}
                      </span>
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Status:</span>{' '}
                      <span
                        className={
                          user.billing?.status === 'active'
                            ? 'text-green-600'
                            : user.billing?.status === 'past_due'
                              ? 'text-red-600'
                              : 'text-gray-600'
                        }
                      >
                        {user.billing?.status || 'none'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Account Dates */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Account Dates</h3>
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="font-medium">Created:</span> {formatDate(user.createdAt)}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Updated:</span> {formatDate(user.updatedAt)}
                    </p>
                  </div>
                </div>

                {/* Stripe Info */}
                {user.billing?.stripeCustomerId && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Stripe</h3>
                    <div className="space-y-1">
                      <p className="text-xs font-mono text-gray-600">
                        {user.billing.stripeCustomerId}
                      </p>
                      {user.billing.stripeSubscriptionId && (
                        <p className="text-xs font-mono text-gray-600">
                          {user.billing.stripeSubscriptionId}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
